import dayjs from 'dayjs'
import { getPlanDisplayName } from '../../../../domain/plans/plan'

/**
 * Every money field arrives as a string, not a number.
 *
 * The ledger stores NUMERIC(12,6) and the chat API serialises it as a string
 * so nothing rounds in transit. Keep it that way: display the string the API
 * sent, and turn it into a number only where arithmetic is unavoidable (chart
 * scales, sorting). Never parse a figure just to re-render it.
 */
export type SpendMetrics = {
  cost_usd: string
  uncached_equivalent_usd: string
  savings_usd: string
  savings_pct: number
  input_tokens: number
  output_tokens: number
  cache_write_tokens: number
  cache_read_tokens: number
  turns: number
  sessions: number
  avg_cost_per_turn: string
  avg_cost_per_session: string
}

/** A valid, all-zero metrics block, for fixtures to splat and override. */
export const defaultSpendMetrics = (): SpendMetrics => ({
  cost_usd: '0.000000',
  uncached_equivalent_usd: '0.000000',
  savings_usd: '0.000000',
  savings_pct: 0,
  input_tokens: 0,
  output_tokens: 0,
  cache_write_tokens: 0,
  cache_read_tokens: 0,
  turns: 0,
  sessions: 0,
  avg_cost_per_turn: '0.000000',
  avg_cost_per_session: '0.000000',
})

export type SpendSummary = {
  from: string
  to: string
  totals: SpendMetrics
  /** Distinct pricing_version values in the window. More than one is a caveat. */
  pricing_versions: string[]
  /** Models with no rate on file. Their uncached comparison is zero. */
  unpriced_models: string[]
  /** When the ledger itself starts. Null when nothing has been recorded. */
  earliest_event_at: string | null
}

export type SpendBucketSize = 'hour' | 'day' | 'week'

export type SpendBucket = SpendMetrics & {
  bucket_start: string
}

export type SpendTimeseries = {
  bucket: SpendBucketSize
  buckets: SpendBucket[]
}

/** `plan` is the plan the tenant was on when each turn happened, not its plan today. */
export type SpendGrouping = 'tenant' | 'model' | 'client' | 'user' | 'plan'

export type SpendBreakdownRow = SpendMetrics & {
  key: string
  label: string
  /**
   * The tenant's integer id, on tenant rows only. Null for a tenant chat
   * can't find in the warehouse; absent from chat builds that predate it.
   */
  tenant_id?: number | null
}

export const defaultSpendBreakdownRow = (): SpendBreakdownRow => ({
  ...defaultSpendMetrics(),
  key: '',
  label: '',
})

export type SpendBreakdown = {
  by: SpendGrouping
  rows: SpendBreakdownRow[]
}

export type SpendFilters = {
  from: string
  to: string
  tenantGuid?: string
  model?: string
  client?: string
  /**
   * A plan key exactly as chat returned it in a plan breakdown row
   * (`PRO_PLAN`, or chat's `(unknown)` bucket). Round-tripped, never built.
   */
  planName?: string
}

export const SPEND_BUCKET_OPTIONS: { value: SpendBucketSize; label: string }[] = [
  { value: 'hour', label: 'Hourly' },
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
]

export const SPEND_GROUPING_OPTIONS: { value: SpendGrouping; label: string }[] = [
  { value: 'tenant', label: 'Tenant' },
  { value: 'model', label: 'Model' },
  { value: 'client', label: 'App' },
  { value: 'user', label: 'User' },
  { value: 'plan', label: 'Plan' },
]

const DEFAULT_WINDOW_DAYS = 30

export function getDefaultSpendFilters(now: dayjs.Dayjs = dayjs()): SpendFilters {
  return {
    from: now.subtract(DEFAULT_WINDOW_DAYS, 'day').startOf('day').toISOString(),
    to: now.endOf('day').toISOString(),
  }
}

/** What the filter form holds; every field is optional until applied. */
export type SpendFormValues = {
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs]
  tenantGuid?: string
  model?: string
  client?: string
  planName?: string
}

/**
 * The form, as the filters every query reads. Dates widen to whole days, and
 * an empty picker falls back to the default window. Blank text fields pass
 * through; buildSpendParams treats a blank as "no filter".
 */
export function toSpendFilters(
  values: SpendFormValues,
  fallback: [dayjs.Dayjs, dayjs.Dayjs],
): SpendFilters {
  return {
    from: (values.dateRange?.[0] ?? fallback[0]).startOf('day').toISOString(),
    to: (values.dateRange?.[1] ?? fallback[1]).endOf('day').toISOString(),
    tenantGuid: values.tenantGuid,
    model: values.model,
    client: values.client,
    planName: values.planName,
  }
}

export function buildSpendParams(
  filters: SpendFilters,
  extra: Record<string, string> = {},
): URLSearchParams {
  const params = new URLSearchParams()
  params.set('from', filters.from)
  params.set('to', filters.to)

  const optional: [string, string | undefined][] = [
    ['tenant_guid', filters.tenantGuid],
    ['model', filters.model],
    ['client', filters.client],
    ['plan_name', filters.planName],
  ]
  for (const [name, value] of optional) {
    // A blank filter is "no filter", not a filter on the empty string.
    if (value && value.trim()) {
      params.set(name, value.trim())
    }
  }

  for (const [name, value] of Object.entries(extra)) {
    params.set(name, value)
  }

  return params
}

/** Chat's key for spend it couldn't attribute to a plan. */
const UNKNOWN_PLAN_KEY = '(unknown)'

/** A plan key from a plan breakdown row, as a display name. */
export function formatPlanLabel(key: string): string {
  return key === UNKNOWN_PLAN_KEY ? 'Unknown plan' : getPlanDisplayName(key)
}

export type PlanOption = { value: string; label: string }

/**
 * Plan filter choices: the plans with spend in the window, as chat ranked
 * them, by display name. The selected plan is kept even if it isn't in the
 * list, so a plan chosen from a breakdown row still shows its name.
 */
export function toPlanOptions(rows: SpendBreakdownRow[], selected?: PlanOption): PlanOption[] {
  const options = rows.map((row) => ({ value: row.key, label: formatPlanLabel(row.key) }))
  if (selected && !options.some((option) => option.value === selected.value)) {
    options.push(selected)
  }
  return options
}

function toNumber(raw: string): number | null {
  if (!raw || !raw.trim()) {
    return null
  }
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/** A total, to cents. */
export function formatUsd(raw: string): string {
  const value = toNumber(raw)
  if (value === null) {
    return '—'
  }
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/**
 * A per-turn or per-session figure, at the six places the ledger stores.
 * Rounding these to cents would show most of them as $0.00.
 */
export function formatUsdPrecise(raw: string): string {
  const value = toNumber(raw)
  if (value === null) {
    return '—'
  }
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  })
}

export function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

export function formatTokens(value: number): string {
  return value.toLocaleString()
}

export type SpendWarning = {
  key: 'coverage' | 'pricing' | 'unpriced'
  message: string
}

/**
 * The caveats the API reports about its own numbers.
 *
 * All three come from the response rather than being restated here — there is
 * no ledger start date and no rate table in this file on purpose. A constant
 * hardcoded here that is also hardcoded in the chat API is two definitions
 * free to drift.
 */
export function getSpendWarnings(
  summary: SpendSummary,
  windowFrom: string,
): SpendWarning[] {
  const warnings: SpendWarning[] = []

  if (!summary.earliest_event_at) {
    warnings.push({
      key: 'coverage',
      message:
        'No inference spend has been recorded yet, so this window is empty rather than zero.',
    })
  } else if (dayjs(summary.earliest_event_at).isAfter(dayjs(windowFrom))) {
    warnings.push({
      key: 'coverage',
      message:
        `Spend has only been recorded since ${dayjs(summary.earliest_event_at).format('MMM D, YYYY')}. ` +
        'This window starts earlier, and the period before that is missing data — not zero spend.',
    })
  }

  if (summary.pricing_versions.length > 1) {
    warnings.push({
      key: 'pricing',
      message:
        `This window spans a pricing change (${summary.pricing_versions.join(', ')}). ` +
        'Actual cost uses the rates in force at the time, while the uncached comparison uses current rates.',
    })
  }

  if (summary.unpriced_models.length > 0) {
    warnings.push({
      key: 'unpriced',
      message:
        `No rate on file for ${summary.unpriced_models.join(', ')}. ` +
        'That spend is counted, but it adds nothing to the uncached comparison, so the saving shown is understated.',
    })
  }

  return warnings
}

export type SpendChartPoint = {
  bucketStart: string
  /**
   * Epoch milliseconds, so the chart can use a real time axis.
   *
   * The API returns only buckets that have data — a period with no spend is
   * absent, not zero. Plotting those against evenly spaced categories would
   * imply continuous usage, and zero-filling them would invent spend figures
   * (and, before the ledger starts, invent them for a period that was never
   * recorded). A time axis places sparse points where they actually fall and
   * leaves the gaps visible as gaps.
   */
  t: number
  label: string
  cost: number
  uncached: number
}

const BUCKET_LABEL_FORMATS: Record<SpendBucketSize, string> = {
  hour: 'MMM D, h A',
  day: 'MMM D',
  week: 'MMM D',
}

export function toChartSeries(
  buckets: SpendBucket[],
  size: SpendBucketSize,
): SpendChartPoint[] {
  return buckets.map((b) => ({
    bucketStart: b.bucket_start,
    t: dayjs(b.bucket_start).valueOf(),
    label: dayjs(b.bucket_start).format(BUCKET_LABEL_FORMATS[size]),
    cost: Number(b.cost_usd),
    uncached: Number(b.uncached_equivalent_usd),
  }))
}

/** Formats an epoch-millisecond axis tick at the granularity being charted. */
export function formatBucketTick(t: number, size: SpendBucketSize): string {
  return dayjs(t).format(BUCKET_LABEL_FORMATS[size])
}
